import debounce from 'lodash.debounce';
import {
  FC,
  PropsWithChildren,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { ResponsiveTable } from '@/components/Table';

const TableWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledResponsiveTable = styled(ResponsiveTable)`
  overflow-x: auto;
`;

const Fade = styled.div<{ $transparent: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 68px;
  z-index: 0;
  border-radius: 4px;
  transition: 0.25s ease-out opacity;
  background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  opacity: ${({ $transparent }) => ($transparent ? 0 : 1)};
`;

interface Props {
  className?: string;
}

const SimpleScrollableTable: FC<PropsWithChildren<Props>> = ({
  children,
  className,
}) => {
  const [isTableEnd, setIsTableEnd] = useState(false);

  const scrollableElementRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const debouncedScrollCallback = useMemo(
    () =>
      debounce((el: EventTarget & HTMLDivElement) => {
        setIsTableEnd(!(el.scrollWidth - el.scrollLeft - 1 > el.clientWidth));
      }, 100),
    [],
  );

  const onTableScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => debouncedScrollCallback(e.currentTarget),
    [debouncedScrollCallback],
  );

  const checkIfHasOverflow = useCallback(() => {
    if (
      scrollableElementRef.current &&
      (scrollableElementRef.current.offsetHeight <
        scrollableElementRef.current.scrollHeight ||
        scrollableElementRef.current.offsetWidth <
          scrollableElementRef.current.scrollWidth)
    ) {
      return setIsTableEnd(false);
    }
    return setIsTableEnd(true);
  }, []);

  useEffect(() => {
    checkIfHasOverflow();

    const current = scrollableElementRef.current;
    if (current) {
      const observer = new ResizeObserver(checkIfHasOverflow);
      observer.observe(current);
      return () => observer.disconnect();
    }
  }, [checkIfHasOverflow]);

  return (
    <div ref={wrapperRef}>
      <TableWrapper>
        <StyledResponsiveTable
          onScroll={onTableScroll}
          className={className}
          ref={scrollableElementRef}
        >
          {children}
        </StyledResponsiveTable>
        {<Fade $transparent={isTableEnd} />}
      </TableWrapper>
    </div>
  );
};

export default SimpleScrollableTable;
